import * as DialogPrimitive from '@radix-ui/react-dialog'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { useFetcher, useLocation, useNavigate } from 'react-router'
import {
	Sheet,
	SheetDescription,
	SheetOverlay,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type CatalogueSkill } from '#app/utils/skill-catalogue.ts'
import { useIsDesktop } from '#app/utils/use-media-query.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { SkillDetail, type SkillDetailContext } from './skill-detail.tsx'

const SkillSheetContext = createContext<(skillId: string) => void>(() => {})

export function useSkillSheet() {
	return useContext(SkillSheetContext)
}

// A content surface, not chrome: `light-content` keeps it off the violet shell.
export function SkillSheetContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
	const isDesktop = useIsDesktop()
	return (
		<DialogPrimitive.Portal>
			<SheetOverlay className='bg-[rgba(26,9,41,.34)] dark:bg-[rgba(4,1,7,.58)]' />
			<DialogPrimitive.Content
				data-slot='sheet-content'
				className={cn(
					'light-content bg-card data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col overflow-hidden duration-200 outline-none print:hidden',
					isDesktop
						? 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 w-full max-w-[512px] border-l shadow-[-18px_0_48px_rgba(26,9,41,.18)] dark:shadow-[-18px_0_48px_rgba(0,0,0,.5)]'
						: 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 top-14 bottom-0 w-full rounded-t-2xl border-t',
					className,
				)}
				{...props}
			>
				{isDesktop ? null : (
					<div className='grid shrink-0 place-items-center pt-2'>
						<span className='bg-border block h-1 w-9 rounded-full' />
					</div>
				)}
				{children}
			</DialogPrimitive.Content>
		</DialogPrimitive.Portal>
	)
}

// Providers nest: the character editor mounts its own to give the sheet a live purchase footer.
export function SkillSheetProvider({
	children,
	ctx = 'sheet',
	footer,
}: {
	children: ReactNode
	ctx?: SkillDetailContext
	footer?: (skill: CatalogueSkill) => ReactNode
}) {
	const [skillId, setSkillId] = useState<string | null>(null)
	const fetcher = useFetcher<{ skill: CatalogueSkill | null }>()
	const user = useOptionalUser()
	const location = useLocation()
	const navigate = useNavigate()

	// The sheet loads from an auth-gated resource: send visitors to log in
	// instead of letting the fetcher follow the redirect mid-page.
	function open(id: string) {
		if (!user) {
			const redirectTo = `${location.pathname}${location.search}`
			void navigate(`/login?${new URLSearchParams({ redirectTo })}`)
			return
		}
		setSkillId(id)
		void fetcher.load(`/resources/skills/${id}`)
	}

	function close() {
		setSkillId(null)
	}

	const skill = fetcher.data?.skill
	const showing = skill && skill.id === skillId ? skill : null

	return (
		<SkillSheetContext.Provider value={open}>
			{children}
			<Sheet
				open={skillId != null}
				onOpenChange={(next) => {
					if (!next) close()
				}}
			>
				<SkillSheetContent>
					{showing ? (
						<SkillDetail
							skill={showing}
							ctx={ctx}
							onSelect={open}
							onClose={close}
							footer={footer?.(showing)}
						/>
					) : (
						<div className='space-y-2 p-5'>
							<SheetTitle className='text-xl'>
								{fetcher.state === 'idle' && fetcher.data
									? 'Compétence introuvable'
									: 'Chargement…'}
							</SheetTitle>
							<SheetDescription className='sr-only'>
								Fiche de compétence
							</SheetDescription>
						</div>
					)}
				</SkillSheetContent>
			</Sheet>
		</SkillSheetContext.Provider>
	)
}
